import * as dateFNS from "date-fns";
import isString from "lodash-es/isString";
import { AccountModel, Meta } from "@/api/chatService";
import {
  AssistantMessageMeta,
  ContentBlock,
} from "@/components/AILanding/chatConnection";

export type ConversationEntry =
  | {
      id: string;
      role: "user";
      text: string;
      timestamp: number;
    }
  | {
      id: string;
      role: "assistant";
      content: ContentBlock[];
      timestamp: number;
      meta?: AssistantMessageMeta;
    }
  | {
      id: string;
      role: "assistant";
      monitoringData: string;
      riskId?: string;
      timestamp: number;
    }
  | {
      id: string;
      role: "assistant";
      error: string;
      timestamp: number;
    }
  | {
      id: string;
      role: "assistant";
      thinking: true;
      timestamp: number;
    }
  | {
      id: string;
      role: "assistant";
      streaming: true;
      streamTitle?: string;
      streamDescription?: string;
      accumulatedText?: string;
      monitoringData?: string;
      timestamp: number;
      processingHistory?: { title: string; description?: string }[];
    };

/**
 * Generate a unique ID for messages
 */
export const generateId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

/**
 * Remove thinking messages from the messages array
 */
export const removeThinkingMessages = (
  messages: ConversationEntry[],
): ConversationEntry[] => {
  return messages.filter(
    message =>
      !(
        message.role === "assistant" &&
        ("thinking" in message || "streaming" in message)
      ),
  );
};

/**
 * Extract text content from a message for copying
 */
export const extractTextFromMessage = (message: ConversationEntry): string => {
  if (message.role === "user" && "text" in message) {
    return message.text;
  }

  if (message.role === "assistant" && "content" in message) {
    return message.content
      .filter(block => block.type === "text")
      .map(block => block.text)
      .join("\n");
  }

  if (message.role === "assistant" && "error" in message) {
    return message.error;
  }

  return "";
};

/**
 * Builds an array of content blocks for assistant responses.
 *
 * @param params - The parameters object
 * @param params.responseResult - The text content of the assistant's response (optional)
 * @param params.meta - Metadata containing actions and content information (optional)
 *
 * @returns An array of content blocks containing text and optional action blocks
 */
export const buildAssistantContentBlocks = (params: {
  responseResult?: string | null;
  meta?: Meta | null;
}): ContentBlock[] => {
  const actions = params.meta?.actions?.map(action => ({
    type: action.type,
    label: action.label,
    action: action.action,
    data: action.data,
  })) as ContentBlock["actions"];

  // Check if this is an error response from meta.content
  const hasErrorInMeta = params.meta?.content?.some(
    (block: Record<string, unknown>) =>
      block.type === "text" && block.isError === true,
  );

  const contentBlocks: ContentBlock[] = [
    {
      type: "text",
      text: params.responseResult ?? "",
      ...(hasErrorInMeta && { isError: true }),
    },
  ];

  if (actions && actions.length > 0) {
    contentBlocks.push({
      type: "actions",
      actions,
    });
  }

  return contentBlocks;
};

/**
 * Builds the `meta` attached to an assistant entry, normalising the several
 * shapes the backend uses to describe the answer's data source into a single
 * `data_source: { id, type }`.
 *
 * @returns the metadata, or `undefined` when no data source can be resolved
 */
export const buildAssistantMessageMeta = (params: {
  meta?: Meta | Record<string, unknown> | null;
  accounts?: AccountModel[] | null;
  dataSourceType?: string | null;
}): AssistantMessageMeta | undefined => {
  const meta = (params.meta ?? undefined) as AssistantMessageMeta | undefined;

  // Prefer an explicit data_source, then the first context account, then the
  // loose `account_id` / `data_source_type` fields.
  const account = params.accounts?.[0];
  const id =
    meta?.data_source?.id ??
    account?.account_id ??
    (meta?.account_id as string | undefined);
  const type =
    meta?.data_source?.type ??
    account?.account_type ??
    params.dataSourceType ??
    (meta?.data_source_type as string | undefined);

  if (!id && !type) {
    return meta;
  }

  return { ...meta, data_source: { id, type } };
};

/**
 * Compliance-note markers emitted by the Guardian agent, e.g.
 *
 * ```
 * ---
 * > 📋 **NOTE**: ... analysis of **EU AI ACT** and **ISO 42001** violations ...
 * > [📊 Schedule Compliance Report →](#compliance-report)
 * ```
 *
 * The leading horizontal rule is optional.
 */
const COMPLIANCE_NOTE_PATTERN =
  /(?:^|\n)(?:---[ \t]*\n+)?>[ \t]*📋\s*\*\*NOTE\*\*:.*?\[.*?Schedule Compliance Report.*?\]\(#compliance-report\)/is;

const COMPLIANCE_FRAMEWORKS_PATTERN =
  /analysis of\s+((?:\*\*[^*]+\*\*(?:,?\s*(?:and\s+)?)?)+)/is;

const BOLD_PATTERN = /\*\*([^*]+)\*\*/g;

/**
 * Extract the compliance note from an assistant markdown block.
 *
 * A single match drives both detection and stripping, so the note is never
 * both rendered as a `ComplianceNote` and left behind as raw markdown (or
 * stripped without anything rendered in its place).
 */
export const extractComplianceNote = (
  text: string,
): {
  hasNote: boolean;
  frameworks: string[];
  cleanedText: string;
} => {
  const noteMatch = text.match(COMPLIANCE_NOTE_PATTERN);

  if (!noteMatch) {
    return { hasNote: false, frameworks: [], cleanedText: text };
  }

  // Framework names are the bold segments following "analysis of" in the note
  const frameworksMatch = noteMatch[0].match(COMPLIANCE_FRAMEWORKS_PATTERN);
  const frameworks = frameworksMatch?.[1]
    ? Array.from(frameworksMatch[1].matchAll(BOLD_PATTERN))
        .map(match => match[1].trim())
        .filter(framework => framework.length > 0)
    : [];

  return {
    hasNote: true,
    frameworks,
    cleanedText: text.replace(noteMatch[0], "").trim(),
  };
};

/**
 * Check if a message is a thinking message
 */
export const isThinkingMessage = (
  message: ConversationEntry,
): message is ConversationEntry & { thinking: true } => {
  return (
    message.role === "assistant" && "thinking" in message && message.thinking
  );
};

/**
 * Check if a message is a user message
 */
export const isUserMessage = (
  message: ConversationEntry,
): message is ConversationEntry & { role: "user"; text: string } => {
  return message.role === "user" && "text" in message;
};

/**
 * Check if a message is an assistant message with content
 */
export const isAssistantContentMessage = (
  message: ConversationEntry,
): message is ConversationEntry & {
  role: "assistant";
  content: ContentBlock[];
  meta?: AssistantMessageMeta;
} => {
  return message.role === "assistant" && "content" in message;
};

/**
 * Check if a message is an assistant message with monitoringData (risk content)
 */
export const isAssistantRiskMessage = (
  message: ConversationEntry,
): message is Extract<
  ConversationEntry,
  { role: "assistant"; monitoringData: string; riskId?: string }
> => {
  return message.role === "assistant" && "monitoringData" in message;
};

/**
 * Check if a message is an assistant error message
 */
export const isAssistantErrorMessage = (
  message: ConversationEntry,
): message is ConversationEntry & { role: "assistant"; error: string } => {
  return message.role === "assistant" && "error" in message;
};

/**
 * Check if a message is a streaming message
 */
export const isStreamingMessage = (
  message: ConversationEntry,
): message is ConversationEntry & { streaming: true } => {
  return (
    message.role === "assistant" && "streaming" in message && message.streaming
  );
};

/**
 * Remove streaming messages from the messages array
 */
export const removeStreamingMessages = (
  messages: ConversationEntry[],
): ConversationEntry[] => {
  return messages.filter(
    message => !(message.role === "assistant" && "streaming" in message),
  );
};

/**
 * Format timestamp to user-friendly string
 * @param timestamp - Timestamp as number (milliseconds) or string (ISO datetime)
 * @returns Formatted timestamp string (e.g., "2:30 PM", "Yesterday 3:45 PM", "Jan 15, 2:30 PM")
 */
export const formatTimestamp = (timestamp: number | string): string => {
  let date: Date;

  if (isString(timestamp)) {
    // Parse datetime string from API using date-fns parseISO
    date = dateFNS.parseISO(timestamp);
  } else {
    // Use number as milliseconds
    date = new Date(timestamp);
  }

  // Check if date is valid using date-fns
  if (!dateFNS.isValid(date)) {
    return "";
  }

  const now = new Date();

  // Format time using date-fns format
  const timeString = dateFNS.format(date, "h:mm a");

  // Check if it's today using date-fns
  if (dateFNS.isToday(date)) {
    return timeString;
  }

  // Check if it's yesterday using date-fns
  if (dateFNS.isYesterday(date)) {
    return `Yesterday ${timeString}`;
  }

  // Check if it's within the last 7 days
  const daysDiff = dateFNS.differenceInDays(now, date);
  if (daysDiff < 7 && daysDiff > 0) {
    const dayName = dateFNS.format(date, "EEE");
    return `${dayName} ${timeString}`;
  }

  // For older messages, show date and time
  // If it's the same year, don't show year
  if (dateFNS.isSameYear(date, now)) {
    return dateFNS.format(date, "MMM d, h:mm a");
  }

  return dateFNS.format(date, "MMM d, yyyy h:mm a");
};
